/**
 * Cross-hook trust marker for channels that an explicit open-channel flow
 * just confirmed via `zero.run()` one-shot query. The navigation guard in
 * `useChannelNavigation` reads its `channels.byUser()` snapshot via the
 * React `useQuery` subscription, which can lag behind a one-shot for
 * several IVM ticks after a mutation. Without this marker, the guard
 * would evict (and sometimes toast) a freshly-opened DM whose membership
 * the server already acknowledged but whose subscription frame hasn't
 * arrived yet.
 *
 * Entries expire after `TRUST_TTL_MS` so a genuinely-deleted channel
 * still gets evicted on the next navigation.
 *
 * Co-located here: `waitForChannelInByUser` — a poll-loop helper for the
 * SAME race. Callers that create-or-resolve a DM should call this BEFORE
 * navigating, so the byUser subscription has caught up by the time the
 * navigation guard runs. `trustChannel` is the belt; this is the braces.
 */

import { queries } from "./queries";

// Loose Zero type — `zero.run(query)` is all we need. Avoids pulling in
// `@rocicorp/zero` types into this module's public surface.
interface ZeroRunner {
  run: (
    q: ReturnType<typeof queries.channels.byUser>,
  ) => Promise<{ id: string }[]>;
}

const TRUST_TTL_MS = 15_000;
const BY_USER_SYNC_ATTEMPTS = 10;
const BY_USER_SYNC_DELAY_MS = 200;

const trustedUntil = new Map<string, number>();

/** Drop entries whose TTL has elapsed. Called on every write so the map
 *  stays bounded by the number of channels opened in the last TTL window. */
function prune(now: number): void {
  for (const [id, expiry] of trustedUntil) {
    if (expiry < now) trustedUntil.delete(id);
  }
}

/** Mark a channel as recently-confirmed by an explicit open flow. */
export function trustChannel(channelId: string): void {
  const now = Date.now();
  prune(now);
  trustedUntil.set(channelId, now + TRUST_TTL_MS);
}

/** True if the channel was trusted within the last 15s. */
export function isChannelTrusted(channelId: string): boolean {
  const expiry = trustedUntil.get(channelId);
  if (expiry === undefined) return false;
  if (expiry < Date.now()) {
    trustedUntil.delete(channelId);
    return false;
  }
  return true;
}

/**
 * Poll `zero.run(queries.channels.byUser())` until `channelId` shows up,
 * or until we hit the attempt cap. This is the same query the navigation
 * guard subscribes to via `useQuery`; running it as a one-shot here lets
 * the caller block until the guard's incoming subscription frame would
 * include this channel.
 *
 * Returns `true` if the channel landed within the attempt window, `false`
 * if we gave up. Callers should still call `trustChannel(id)` as a belt
 * to handle the case where the subscription is taking unusually long.
 *
 * Total worst-case wait: `BY_USER_SYNC_ATTEMPTS * BY_USER_SYNC_DELAY_MS`
 * = 10 × 200ms = 2s. The first attempt has no delay so a typical
 * already-replicated channel resolves immediately.
 */
export async function waitForChannelInByUser(
  zero: ZeroRunner,
  channelId: string,
): Promise<boolean> {
  for (let attempt = 0; attempt < BY_USER_SYNC_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, BY_USER_SYNC_DELAY_MS));
    }
    try {
      const channels = await zero.run(queries.channels.byUser());
      if (channels.some((c) => c.id === channelId)) return true;
    } catch {
      // Zero query failed — retry
    }
  }
  return false;
}
